/**
 * Shared rules for bid / counter-offer amount fields: Indian comma grouping only,
 * block obvious contact info (letters, @, odd punctuation), and block values whose
 * digits match common Indian mobile patterns (including values typed with hyphens/spaces,
 * which collapse to the same digit string).
 */

export const BID_AMOUNT_CONTACT_BLOCKED_MSG =
  "Enter a rupee amount using digits and Indian-style commas (for example 20,000). Phone numbers, email, or other text are not allowed.";

const MAX_DIGITS = 15;

/** Allowed while typing: digits, Indian/ASCII comma, spaces, NBSP, rupee symbol. */
const ALLOWED_TYPING_CHARS = /^[\d,，\s\u00A0₹]*$/u;

/**
 * @param {string} raw
 * @returns {boolean} true if raw contains symbols we refuse (hyphens, letters, @, dots, etc.)
 */
export function bidAmountRawHasDisallowedSymbols(raw) {
  const s = String(raw ?? "");
  if (!s) return false;
  if (!ALLOWED_TYPING_CHARS.test(s)) return true;
  if (/[a-zA-Z@]/.test(s)) return true;
  return false;
}

/**
 * Strip to digits, format as grouped integer (en-IN). Empty string if no digits.
 * @param {string} raw
 */
export function formatIndianBidAmountInput(raw) {
  const digits = String(raw ?? "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length > MAX_DIGITS) return formatIndianBidAmountInput(digits.slice(0, MAX_DIGITS));
  const n = Number(digits);
  if (!Number.isFinite(n) || n <= 0) return "";
  return n.toLocaleString("en-IN");
}

export function bidAmountInputToNumber(formatted) {
  const digits = String(formatted ?? "").replace(/\D/g, "");
  if (!digits) return NaN;
  if (digits.length > MAX_DIGITS) return NaN;
  return Number(digits);
}

export function bidAmountDigitsOnly(formatted) {
  return String(formatted ?? "").replace(/\D/g, "");
}

/**
 * @param {string} digitString digits only
 * @returns {string|null} error message or null if OK
 */
export function validateBidAmountDigitsNotPhoneLike(digitString) {
  const d = String(digitString ?? "").replace(/\D/g, "");
  if (!d) return null;
  if (d.length > MAX_DIGITS) {
    return "That amount is too large. Enter a smaller value.";
  }
  const n = Number(d);
  if (!Number.isFinite(n) || n <= 0) return null;

  const normalized = String(Math.trunc(n));
  const check = normalized;

  if (check.length === 10 && /^[6-9]\d{9}$/.test(check)) {
    return BID_AMOUNT_CONTACT_BLOCKED_MSG;
  }
  if (check.length === 11 && /^0[6-9]\d{9}$/.test(check)) {
    return BID_AMOUNT_CONTACT_BLOCKED_MSG;
  }
  if (check.length === 12 && /^91[6-9]\d{9}$/.test(check)) {
    return BID_AMOUNT_CONTACT_BLOCKED_MSG;
  }
  return null;
}
